/**
 * BloccoPreventiviOfficina — componente condiviso
 *
 * Estratto da FaseAnalisi.tsx (renderQuoteSummary righe 287–369 e
 * Modal motivo righe 744–765). Mostra la sezione "Officina esterna"
 * per un intervento:
 *   - elenco preventivi con tag stato, importo, IVA, giorni lavorazione
 *   - pulsanti Approva / Respingi / Rinegozia (quando isActionable)
 *   - Modal motivo per Respingi / Rinegozia (stato interno)
 *
 * Restituisce null se canViewQuotes è false o non ci sono preventivi.
 * getQuoteTag è re-esportato per retrocompatibilità con PreventiviOfficinePage.
 */
import React, { useState } from 'react'
import {
  Button, Card, Input, Modal, Space, Tag, theme, Typography,
} from 'antd'
import dayjs from 'dayjs'
import {
  useApprovaQuote, useRespingiQuote, useRinegoziaQuote,
} from '@/hooks/useWorkshopQuotes'
import type { WorkshopQuote, WorkshopQuoteStato } from '@/types/workshopQuotes'

const { Text } = Typography

// ── Costanti stato preventivo ─────────────────────────────────────────────────
// Erano in FaseAnalisi.tsx a livello modulo — spostate qui perché
// usate solo dalla logica di questo blocco.
const OPEN_QUOTE_STATES: WorkshopQuoteStato[] = ['inviata', 'sopralluogo_richiesto', 'risposta_ricevuta']

// ── Funzione tag ──────────────────────────────────────────────────────────────
// Era export in FaseAnalisi — re-esportata da lì per retrocompatibilità.
export function getQuoteTag(q: { stato: WorkshopQuoteStato; data_scadenza_risposta: string | null }): { label: string; color: string } {
  if (q.stato === 'inviata') {
    const expired = q.data_scadenza_risposta && dayjs(q.data_scadenza_risposta).isBefore(dayjs(), 'day')
    return expired ? { label: 'Scaduta', color: 'red' } : { label: 'In attesa', color: 'default' }
  }
  if (q.stato === 'sopralluogo_richiesto') return { label: 'Sopralluogo', color: 'gold' }
  if (q.stato === 'risposta_ricevuta') return { label: 'Da valutare', color: 'gold' }
  if (q.stato === 'approvata') return { label: 'Approvata', color: 'default' }
  if (q.stato === 'respinta') return { label: 'Respinta', color: 'default' }
  if (q.stato === 'rifiutata_officina') return { label: 'Rifiutata', color: 'red' }
  return { label: 'Annullata', color: 'default' }
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface BloccoPreventiviOfficinaProps {
  workOrderId: number
  /** Preventivi già filtrati per l'intervento — il padre passa
   *  interventionQuoteMap.get(record.id) ?? [] */
  quotes: WorkshopQuote[]
  canViewQuotes: boolean
  canApprove: boolean
}

// ── Componente ────────────────────────────────────────────────────────────────
const BloccoPreventiviOfficina: React.FC<BloccoPreventiviOfficinaProps> = ({
  workOrderId,
  quotes,
  canViewQuotes,
  canApprove,
}) => {
  const { token } = theme.useToken()

  // Le tre mutation vengono create qui: identico a quanto faceva FaseAnalisi.
  const approvaMutation        = useApprovaQuote(workOrderId)
  const respingiMutation       = useRespingiQuote(workOrderId)
  const rinegoziaQuoteMutation = useRinegoziaQuote(workOrderId)

  // Stato interno Modal motivo (era in FaseAnalisi)
  const [quoteNotaModal, setQuoteNotaModal] = useState<{ action: 'respingi' | 'rinegozia'; quoteId: number } | null>(null)
  const [quoteNotaText, setQuoteNotaText]   = useState('')

  const handleQuoteNotaConfirm = async () => {
    if (!quoteNotaModal || !quoteNotaText.trim()) return
    const body = { motivo: quoteNotaText.trim() }
    if (quoteNotaModal.action === 'respingi') {
      await respingiMutation.mutateAsync({ quoteId: quoteNotaModal.quoteId, body })
    } else {
      await rinegoziaQuoteMutation.mutateAsync({ quoteId: quoteNotaModal.quoteId, body })
    }
    setQuoteNotaModal(null)
    setQuoteNotaText('')
  }

  if (!canViewQuotes || quotes.length === 0) return null

  const sorted = [...quotes].sort((a, b) => {
    const aActive = OPEN_QUOTE_STATES.includes(a.stato) || a.stato === 'approvata' || a.stato === 'respinta' || a.stato === 'rifiutata_officina'
    const bActive = OPEN_QUOTE_STATES.includes(b.stato) || b.stato === 'approvata' || b.stato === 'respinta' || b.stato === 'rifiutata_officina'
    if (aActive && !bActive) return -1
    if (!aActive && bActive) return 1
    return (b.created_at ?? '').localeCompare(a.created_at ?? '')
  })

  return (
    <>
      <Card
        size="small"
        title={<span style={{ fontSize: 12 }}>Officina esterna</span>}
        styles={{ body: { padding: 8 } }}
      >
        <div>
          {sorted.map((q) => {
            const { label, color } = getQuoteTag(q)
            const isActionable = q.stato === 'risposta_ricevuta' && q.accettato_da_officina === true && canApprove

            return (
              <div key={q.id} style={{ marginBottom: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <Tag color={color} style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                    {label}
                  </Tag>
                  <Text style={{ fontSize: 11 }}>{q.workshop_nome ?? '—'}</Text>
                  {q.importo != null && (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {`€ ${Number(q.importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                      {q.aliquota_iva != null ? ` + IVA ${q.aliquota_iva}%` : ''}
                      {q.giorni_lavorazione != null ? ` · ${q.giorni_lavorazione}gg` : ''}
                    </Text>
                  )}
                  {q.importo != null && (
                    <Tag color={q.ricambi_inclusi ? 'orange' : 'default'} style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                      {q.ricambi_inclusi ? 'Ricambi inclusi' : 'Solo manodopera'}
                    </Tag>
                  )}
                  {isActionable && (
                    <Space size={4} style={{ marginLeft: 'auto' }}>
                      <Button
                        size="small"
                        type="primary"
                        style={{ fontSize: 11, height: 22 }}
                        loading={approvaMutation.isPending}
                        onClick={() => approvaMutation.mutate({ quoteId: q.id, body: {} })}
                      >
                        Approva
                      </Button>
                      <Button
                        size="small"
                        danger
                        style={{ fontSize: 11, height: 22 }}
                        onClick={() => { setQuoteNotaText(''); setQuoteNotaModal({ action: 'respingi', quoteId: q.id }) }}
                      >
                        Respingi
                      </Button>
                      <Button
                        size="small"
                        style={{ fontSize: 11, height: 22 }}
                        onClick={() => { setQuoteNotaText(''); setQuoteNotaModal({ action: 'rinegozia', quoteId: q.id }) }}
                      >
                        Rinegozia
                      </Button>
                    </Space>
                  )}
                </div>
                {q.note_officina && (
                  <div style={{ fontSize: 10, color: token.colorTextSecondary, marginTop: 1 }}>
                    <strong>Officina:</strong> {q.note_officina}
                  </div>
                )}
                {q.motivo_rifiuto && q.revisione > 0 && (
                  <div style={{ fontSize: 10, color: token.colorTextSecondary, marginTop: 1 }}>
                    <strong>Rinegoziato:</strong> {q.motivo_rifiuto}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Card>

      {/* Modal motivo — era nel JSX di FaseAnalisi (righe 744-765) */}
      <Modal
        open={!!quoteNotaModal}
        title={quoteNotaModal?.action === 'respingi' ? 'Motivo del rifiuto' : 'Motivo della rinegoziazione'}
        okText="Conferma"
        cancelText="Annulla"
        okButtonProps={{
          disabled: !quoteNotaText.trim(),
          danger: quoteNotaModal?.action === 'respingi',
          loading: respingiMutation.isPending || rinegoziaQuoteMutation.isPending,
        }}
        onOk={handleQuoteNotaConfirm}
        onCancel={() => { setQuoteNotaModal(null); setQuoteNotaText('') }}
        destroyOnClose
      >
        <Input.TextArea
          rows={3}
          placeholder="Nota obbligatoria..."
          value={quoteNotaText}
          onChange={e => setQuoteNotaText(e.target.value)}
          style={{ marginTop: 8 }}
        />
      </Modal>
    </>
  )
}

export default BloccoPreventiviOfficina
